/**
 * The car's mileage, from logged odometer readings.
 *
 * This replaces the old estimate that recomputed itself out of raw cost lines
 * on every request. Two things moved:
 *
 * 1. **Which car a reading belongs to is decided when it is written**, not when
 *    it is read. A fuel card moves between cars and carries another car's
 *    odometer with it, so the dated koppeling still decides — but once, at
 *    import (and again from `hersyncMetingen` when a koppeling is corrected).
 * 2. **A rejected reading is kept, marked.** Drivers type filler like 888888 and
 *    966142; three such values sit in the fleet data inside valid koppeling
 *    windows. Those get `betrouwbaar = false` with a note instead of being
 *    filtered out afresh every time, so a re-import cannot quietly bring them
 *    back and you can still see that someone typed it.
 *
 * What is left here is arithmetic over trusted readings, which is why this is
 * the only module the figure, the chart and the per-card split all read. There
 * used to be three separate answers to "which reading may count": the card, the
 * chart in `CarDashboard` and this heuristic each had their own.
 */
/** No passenger car in this fleet reaches this; above it, it is a typo. */
export const KM_PLAFOND = 300_000;
const KM_STAND = 'KM_STAND';
function bruikbaar(m) {
    return m.soort === KM_STAND && m.betrouwbaar && m.waarde > 0;
}
/**
 * Is this reading physically impossible? The default verdict at import time.
 *
 * Only the plainly absurd: at or above the ceiling, or not a positive number.
 * The ceiling is the one rule real data justifies — drivers type filler like
 * 888888 and 966142, the fleet's highest genuine reading is 194.796 km, and cars
 * are sold well before 300.000.
 *
 * Deliberately **no relative rule** against the previous reading. A "this is
 * more than ten times the last one" test was tried and removed: it cascades. The
 * previous reading only advances when a reading is accepted, so one stray low
 * value (a driver typing the trip meter, or a new car delivered at 12 km)
 * becomes the baseline and then rejects every genuine reading after it, leaving
 * the car reporting the stray value forever. Keeping only the ceiling also makes
 * this agree exactly with `backfill-metingen.sql`, so a reading imported today
 * gets the same verdict as the same reading re-imported tomorrow.
 *
 * A smaller slip (138000 typed while the car sat at ~133500) is indistinguishable
 * from a real reading, so it passes and shows up as a line running ahead until
 * the odometer catches up. `kmPerMaand` drops backward steps for display; that is
 * a charting question, not a trust question.
 */
export function verdachteMeting(waarde) {
    if (!Number.isFinite(waarde) || waarde <= 0)
        return true;
    if (waarde >= KM_PLAFOND)
        return true;
    return false;
}
/**
 * The current mileage: the highest trusted reading, with the date and source it
 * came from. Null when nothing qualifies — still the normal case for a car that
 * has neither a fuel-card reading nor a hand-entered one.
 *
 * Highest rather than most recent, because an odometer does not run backwards:
 * a lower reading dated later is the typo, not the mileage. That also makes a
 * hand-entered reading behave predictably — it wins as soon as it is the
 * highest, and when it is not, the card should say which reading beat it.
 */
export function huidigeKmStand(metingen) {
    let beste = null;
    let aantal = 0;
    for (const m of metingen) {
        if (!bruikbaar(m))
            continue;
        aantal++;
        // On an equal reading the later date wins, so "since when" does not lag.
        if (!beste || m.waarde > beste.waarde || (m.waarde === beste.waarde && m.datum > beste.datum)) {
            beste = m;
        }
    }
    if (!beste)
        return null;
    return { kmStand: beste.waarde, datum: beste.datum, metingen: aantal, bron: beste.bron };
}
/**
 * The odometer per month, oldest first: one point per month holding the highest
 * reading in it, which is the meter at the end of that month.
 *
 * One point per month rather than one per reading, because a month can hold
 * half a dozen fill-ups and those cluster into an unreadable knot — for G382XB
 * 36 readings become 8 monthly points. Months whose reading goes backwards drop
 * out: a meter does not run back, so one of the two is a typo and the line
 * would bounce. For G382XB that is May 2026 (137.500 against 138.000 in April).
 * Because the chart's x-axis runs on months and not on the number of points,
 * a dropped month looks exactly like a month without a reading.
 */
export function kmPerMaand(metingen) {
    const hoogste = new Map();
    for (const m of metingen) {
        if (!bruikbaar(m) || !m.datum)
            continue;
        const maand = m.datum.slice(0, 7);
        hoogste.set(maand, Math.max(hoogste.get(maand) ?? 0, m.waarde));
    }
    const punten = [];
    for (const maand of [...hoogste.keys()].sort()) {
        const km = hoogste.get(maand);
        const vorige = punten[punten.length - 1];
        if (vorige && km < vorige.km)
            continue;
        punten.push({ maand, km, erbij: vorige ? km - vorige.km : 0 });
    }
    if (punten.length < 2)
        return { punten, gereden: 0, perMaand: null };
    const eerste = punten[0];
    const laatste = punten[punten.length - 1];
    const spanne = Math.max(1, maandIndex(laatste.maand) - maandIndex(eerste.maand));
    const gereden = laatste.km - eerste.km;
    return { punten, gereden, perMaand: gereden / spanne };
}
/**
 * Kilometres driven per card, for the card detail page: per card the span
 * between its lowest and highest trusted reading.
 *
 * Not the sum of the steps between readings, because a card only sees part of
 * the car's life — the kilometres driven while another card was on the car sit
 * in the gap and are not this card's. Cards with a single reading contribute 0.
 * The key is `cardId`, with null collecting the hand-entered readings.
 */
export function kmPerPas(metingen) {
    const grenzen = new Map();
    for (const m of metingen) {
        if (!bruikbaar(m))
            continue;
        const g = grenzen.get(m.cardId);
        if (!g)
            grenzen.set(m.cardId, { min: m.waarde, max: m.waarde });
        else {
            if (m.waarde < g.min)
                g.min = m.waarde;
            if (m.waarde > g.max)
                g.max = m.waarde;
        }
    }
    const uit = new Map();
    for (const [cardId, g] of grenzen)
        uit.set(cardId, g.max - g.min);
    return uit;
}
/** `YYYY-MM` → months since epoch, so month maths stays integer arithmetic. */
function maandIndex(maand) {
    const [j, m] = maand.split('-');
    return Number(j) * 12 + (Number(m) - 1);
}
//# sourceMappingURL=meting.js.map